import { resolveNfcCapabilities } from '../../../utils/resolveNfcCapabilities';
import {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import type { ReactNode } from 'react';
import {
  Button,
  Container,
  Flex,
  Spacer,
  Text,
  Icon,
  Grid,
  GridItem,
  Box,
  useDisclosure,
  Spinner,
  Center,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import Breadcrumbs from '../../../common/Breadcrumbs';
import { TabsNudgeBannerContent } from './TabsNudgeBanner';
import { CustomBulbIcon } from '../../../theme/icons/CustomBulbIcon';
import DesignGuideButton from './DesignGuideButton';
import DesignGuideModal from './modals/DesignGuideModal';
import BuilderTabs from './BuilderTabs';
import BuilderPanel from './BuilderPanel';
import PreviewPanel from './PreviewPanel';
import { DemoPassContext } from './DemoPassContext';
import { EventDetailsBannerContent } from './EventDetailsBanner';
import InfoBannerQueue from './InfoBannerQueue';
import {
  resetErrors,
  setError,
} from '../../../store/redux/slices/templateErrors.slice';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  hasNonNumericLoyaltyBalance,
  LOYALTY_BALANCE_ERROR_MESSAGE,
} from './utils/fieldHelpers';
import type { RootState } from '../../../store/redux/store';
import {
  setFormCreateSuccessId,
  setFormWasDisabledOnLoad,
} from '../../../store/redux/slices/template.slice';
import InspirationModal from '../../modals/InspirationModal';
import { convertFieldIdsToSnakeCase } from './utils/formatTemplatePayload';
import {
  resetTemplate,
  setNotificationHeaderDefault,
  setTemplate,
  setOriginalDefaultValues,
  setNfcFeature,
  setFromPreset,
  setPresetId,
  setSimpleField,
  setFocusedField,
  setFieldProp,
} from '../../../store/redux/slices/template.slice';
import { resetForm, setForm } from '../../../store/redux/slices/form.slice';
import { useParams } from 'react-router';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useGetPassTemplateQuery } from '../../../store/redux/api/templateApi';
import { useGetOnboardingStateQuery } from '../../../store/redux/api/onboardingApi';
import {
  mapApiResponseToTemplateState,
  mapApiResponseToFormState,
  buildOriginalDefaultValues,
} from './utils/mapApiResponseToTemplateState';
import { getPresetById } from '../../../constants/templatePresets';
import { MeContext } from '../../../context/MeContext';
import { MeContextState, Entity } from '../../../store/types/account';
import ErrorsBanner from './ErrorsBanner';
import EnhancedDesignTryBanner from './EnhancedDesignTryBanner';
import CustomFormBuilderPanel from './custom-download-forms/CustomFormBuilderPanel';
import CustomFormPreviewPanel from './custom-download-forms/CustomFormPreviewPanel';
import FormIsLiveModal from './modals/FormIsLiveModal';
import NoFormFieldsModal from './modals/NoFormFieldsModal';
import UnsavedChangesModal from './UnsavedChangesModal';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';
import { useTemplateLimitReached } from '../../../hooks/useTemplateLimitReached';
import { guardPublishClick } from './utils/guardPublishClick';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';
import LockedActionButton from '../../common/LockedActionButton';
import {
  BUILDER_DEVICE_TABS,
  BUILDER_PASS_TAB,
  BUILDER_PASS_TABS,
  type BuilderDeviceTab,
  type BuilderPassTab,
} from './utils/templateBuilderTabs';

interface PassTemplateInContext {
  id?: string;
  uuid?: string;
  entity_id?: string | number;
}

interface EntityWithId extends Entity {
  id?: string | number;
}

const PassTemplateBuilderPage = ({ isEdit }: { isEdit?: boolean }) => {
  const [selectedTab, setSelectedTab] = useState<BuilderDeviceTab>(
    BUILDER_DEVICE_TABS[0]
  );

  const dispatch = useAppDispatch();
  const isCreating = useAppSelector((state) => state.template._isCreating);
  const isCreatingRef = useRef(isCreating);
  isCreatingRef.current = isCreating;
  const templateStatus = useAppSelector((state) => state.template.status);
  const formCreateSuccessId = useAppSelector(
    (state) => state.template._formCreateSuccessId
  );
  const formWasDisabledOnLoad = useAppSelector(
    (state: RootState) =>
      (state.template as unknown as { _formWasDisabledOnLoad: boolean })
        ._formWasDisabledOnLoad
  );
  const templateType = useAppSelector((state) => state.template.templateType);
  const templateName = useAppSelector((state) => state.template.templateName);
  const fromPreset = useAppSelector((state) => state.template._fromPreset);
  const nfcGoogleNfc = useAppSelector((state) => state.template.nfc.googleNfc);
  const hasDynamicForm =
    useAppSelector((state: RootState) => state.form.enabled) === true;
  const hasAutoSetFilterEntity = useRef(false);
  const lastHydratedTemplateId = useRef<string | null>(null);
  const presetAppliedRef = useRef(false);

  const { state: meStateRaw, updateFilterEntity } = useContext(MeContext) as {
    state: MeContextState & {
      onboardingChecklistEnabled: boolean | null;
      onboardingCompleted: boolean | null;
    };
    updateFilterEntity: (entity: Entity | null) => void;
  };
  const meState = meStateRaw;
  const filterEntity: Entity | null =
    meState.entities?.length === 1
      ? meState.entities[0]
      : (meState.filterEntity ?? null);
  const hasMultipleEntities = (meState.entities?.length ?? 0) > 1;
  const needsEntitySelection = !isEdit && hasMultipleEntities && !filterEntity;

  const onboardingChecklistEnabled = meState.onboardingChecklistEnabled;
  const onboardingCompleted = meState.onboardingCompleted;
  const { data: onboardingState } = useGetOnboardingStateQuery(undefined, {
    skip: !onboardingChecklistEnabled || !!onboardingCompleted,
  });
  const mustPublishForOnboarding =
    !!onboardingChecklistEnabled &&
    !onboardingCompleted &&
    onboardingState?.steps.template_created !== true;

  // Only a confirmed draft offers Publish; hidden or not-yet-hydrated doesn't.
  const isPublishedEdit = isEdit && templateStatus !== 'draft';
  // Issuance forms only after publish (or for hidden). Wait for status so drafts
  // don't flash the live form while the template is still loading.
  const issuanceFormStatusPending = isEdit && templateStatus == null;
  const canEditIssuanceForm =
    templateStatus === 'published' || templateStatus === 'hidden';
  // A submit can arrive without a button click (Enter key, Save and exit), so the
  // ref has to hold the current mode's intent rather than a hardcoded fallback.
  const defaultSaveIntent: 'draft' | 'publish' | 'save' = isPublishedEdit
    ? 'save'
    : mustPublishForOnboarding
      ? 'publish'
      : 'draft';
  const saveIntentRef = useRef(defaultSaveIntent);
  useEffect(() => {
    saveIntentRef.current = defaultSaveIntent;
  }, [defaultSaveIntent]);

  // Image uploads stay highlighted on hover, so clear focus when clicking outside a field.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-template-field]')) return;
      dispatch(setFocusedField(null));
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [dispatch]);

  const { isLimitReached } = useTemplateLimitReached();
  const isReadOnly = useIsReadOnly();
  const readOnlyInertProps = isReadOnly
    ? ({ inert: '' } as unknown as { inert?: boolean })
    : {};
  const [publishBlockedOpen, setPublishBlockedOpen] = useState(false);

  const { uuid: templateId } = useParams<{ uuid: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const presetId = (location.state as { presetId?: string } | null)?.presetId;
  const [formHydratedFromApi, setFormHydratedFromApi] = useState(false);

  const {
    data: templateResponse,
    isSuccess,
    isLoading: isLoadingTemplate,
  } = useGetPassTemplateQuery(
    {
      id: templateId as string,
      includeDynamicForm: true,
      includePassesCount: true,
    },
    {
      skip: !isEdit || !templateId,
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    if (isSuccess && templateResponse) {
      try {
        dispatch(resetErrors());

        const templateData = templateResponse.data as Parameters<
          typeof mapApiResponseToTemplateState
        >[0];
        const templateState = mapApiResponseToTemplateState(templateData);
        dispatch(setTemplate(templateState));
        dispatch(setFromPreset(false));
        dispatch(setPresetId(null));
        // Re-set loyalty balance error if the loaded template has non-numeric value
        if (
          hasNonNumericLoyaltyBalance(
            templateState.loyaltyBalance?.enabled,
            templateState.fields?.header?.one?.defaultValue
          )
        ) {
          dispatch(
            setError({
              path: 'loyaltyBalance.defaultValue',
              message: LOYALTY_BALANCE_ERROR_MESSAGE,
            })
          );
        }
        const passesCount =
          (templateResponse.data as any)?.attributes?.passesCount ?? 0;
        dispatch(setSimpleField({ field: '_passesCount', value: passesCount }));

        const formState = mapApiResponseToFormState(templateResponse);
        dispatch(setForm(formState));
        dispatch(setFormWasDisabledOnLoad(!formState.enabled));

        dispatch(
          setOriginalDefaultValues(
            buildOriginalDefaultValues(templateState.fields)
          )
        );
      } finally {
        lastHydratedTemplateId.current = templateId ?? null;
        setFormHydratedFromApi(true);
      }
    }
  }, [dispatch, isSuccess, templateResponse, templateId]);

  // Reset the auto-set flag and form hydration when template changes
  useEffect(() => {
    hasAutoSetFilterEntity.current = false;
    if (lastHydratedTemplateId.current !== templateId) {
      lastHydratedTemplateId.current = null;
      setFormHydratedFromApi(false);
    }
  }, [templateId]);

  // The template response is authoritative; the me-context list can predate a new template.
  const templateOwnerId =
    (templateResponse?.data?.attributes as { entityId?: string | number })
      ?.entityId ??
    (
      meState as MeContextState & {
        passTemplates?: PassTemplateInContext[];
      }
    ).passTemplates?.find(
      (item) => item.uuid === templateId || item.id === templateId
    )?.entity_id;

  // Auto-set filterEntity to the template's entity when editing (separate effect to avoid re-running template setup)
  useEffect(() => {
    if (isSuccess && templateResponse && !hasAutoSetFilterEntity.current) {
      const entities = (
        meState as MeContextState & {
          entities?: EntityWithId[];
        }
      ).entities;
      if (templateOwnerId != null && entities) {
        const templateEntity = entities.find(
          (e: EntityWithId) => String(e.id) === String(templateOwnerId)
        );
        if (templateEntity) {
          hasAutoSetFilterEntity.current = true;
          updateFilterEntity(templateEntity);
        }
      }
    }
  }, [
    isSuccess,
    templateResponse,
    meState,
    templateOwnerId,
    updateFilterEntity,
  ]);

  // Only reset unsupported legacy values after resolving the template's owner.
  useEffect(() => {
    const context = meState as MeContextState & {
      entities?: EntityWithId[];
    };
    const entity = isEdit
      ? context.entities?.find(
          (item) =>
            templateOwnerId != null &&
            String(item.id) === String(templateOwnerId)
        )
      : filterEntity;
    const { isResolved, googleNfcAvailable } = resolveNfcCapabilities(entity);
    if (
      formHydratedFromApi &&
      isResolved &&
      !googleNfcAvailable &&
      nfcGoogleNfc
    ) {
      dispatch(setNfcFeature({ googleNfc: false }));
    }
  }, [
    formHydratedFromApi,
    meState,
    templateOwnerId,
    isEdit,
    filterEntity,
    nfcGoogleNfc,
    dispatch,
  ]);

  useEffect(() => {
    if (isEdit) return;

    const preset = presetId ? getPresetById(presetId) : undefined;
    if (preset && !presetAppliedRef.current) {
      presetAppliedRef.current = true;
      const templateState = mapApiResponseToTemplateState({
        id: '',
        type: 'passTemplate',
        attributes: preset.template_data,
      } as unknown as Parameters<typeof mapApiResponseToTemplateState>[0]);
      const fields = convertFieldIdsToSnakeCase(templateState.fields);
      dispatch(resetTemplate());
      dispatch(setTemplate({ ...templateState, fields, templateName: '' }));
      dispatch(setFromPreset(true));
      dispatch(setPresetId(presetId ?? null));
      dispatch(
        setOriginalDefaultValues(
          buildOriginalDefaultValues(templateState.fields)
        )
      );
      dispatch(resetForm());
      dispatch(resetErrors());
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: null,
      });
      return;
    }

    if (presetAppliedRef.current) return;

    dispatch(resetTemplate());
    dispatch(resetForm());
    if (filterEntity) {
      dispatch(setNotificationHeaderDefault({ entityName: filterEntity.name }));
    }
    dispatch(resetErrors());
  }, [
    dispatch,
    isEdit,
    filterEntity,
    presetId,
    navigate,
    location.pathname,
    location.search,
  ]);

  const showDesignGuide = templateType !== 'stampCard';
  const isTemplateReady = isEdit ? formHydratedFromApi : !needsEntitySelection;
  const { isBlocked, proceed, reset, markClean } =
    useUnsavedChanges(isTemplateReady);
  const onSaveCompleteRef = useRef<(() => void) | null>(null);

  const {
    isOpen: isInspirationModalOpen,
    onOpen: onOpenInspirationModal,
    onClose: onCloseInspirationModal,
  } = useDisclosure();
  const {
    isOpen: isDesignGuideOpen,
    onOpen: onOpenDesignGuide,
    onClose: onCloseDesignGuide,
  } = useDisclosure();

  const [selectedPassTab, setSelectedPassTab] = useState<BuilderPassTab>(
    BUILDER_PASS_TABS[0]
  );
  const [visitedPassTabs, setVisitedPassTabs] = useState<Set<BuilderPassTab>>(
    new Set([BUILDER_PASS_TABS[0]])
  );
  const handleSetSelectedPassTab = useCallback((tab: BuilderPassTab) => {
    setSelectedPassTab(tab);
    setVisitedPassTabs((prev) => new Set(prev).add(tab));
  }, []);
  const showTabsNudge =
    !isEdit &&
    !visitedPassTabs.has(BUILDER_PASS_TAB.BACK) &&
    !visitedPassTabs.has(BUILDER_PASS_TAB.FEATURES);
  const [isEnhancedTabActive, setIsEnhancedTabActive] = useState(false);
  const [googleRevertedPreview, setGoogleRevertedPreview] = useState(false);
  const [isGooglePreviewActive, setIsGooglePreviewActive] = useState(false);
  const infoBanners = useMemo(() => {
    const banners: { id: string; content: ReactNode }[] = [];
    if (showTabsNudge) {
      banners.push({
        id: 'tabsNudge',
        content: (
          <TabsNudgeBannerContent onNavigateToTab={handleSetSelectedPassTab} />
        ),
      });
    }
    if (isEnhancedTabActive) {
      banners.push({
        id: 'eventDetails',
        content: <EventDetailsBannerContent />,
      });
    }
    return banners;
  }, [showTabsNudge, isEnhancedTabActive, handleSetSelectedPassTab]);
  const [isDemoPass, setIsDemoPass] = useState(false);
  const [selectedMainTabIndex, setSelectedMainTabIndex] = useState(() =>
    searchParams.get('tab') === 'form' ? 1 : 0
  );
  const [noFormFieldsModalOpen, setNoFormFieldsModalOpen] = useState(false);
  const skipNoFormFieldsCheckRef = useRef(false);
  const [pendingExpandLocationIndex, setPendingExpandLocationIndex] = useState<
    number | null
  >(null);
  const handlePendingExpandLocationConsumed = useCallback(() => {
    setPendingExpandLocationIndex(null);
  }, []);

  useEffect(() => {
    if (searchParams.get('tab') === 'form') {
      setSelectedMainTabIndex(1);
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('passTab') !== 'features') return;
    const featuresTab = BUILDER_PASS_TABS.find(
      (t) => t.toLowerCase() === 'features'
    );
    if (featuresTab) handleSetSelectedPassTab(featuresTab);
    const next = new URLSearchParams(searchParams);
    next.delete('passTab');
    setSearchParams(next, { replace: true });
  }, [searchParams]);

  useEffect(() => {
    if (
      canEditIssuanceForm &&
      formCreateSuccessId != null &&
      selectedMainTabIndex !== 1
    ) {
      setSelectedMainTabIndex(1);
    }
  }, [formCreateSuccessId, selectedMainTabIndex, canEditIssuanceForm]);

  const breadcrumbLinks = [
    {
      linkName: 'Home',
      linkUrl: '/',
    },
    {
      linkName: 'pass templates',
      linkUrl: '/pass-templates/',
    },
    {
      linkName:
        selectedMainTabIndex === 1
          ? isEdit
            ? 'edit issuance form'
            : 'create a new issuance form'
          : isEdit
            ? 'edit pass template'
            : 'create a new pass template',
      linkUrl: null,
    },
  ];

  const renderActionButtons = () => {
    const setIntent = (intent: 'draft' | 'publish' | 'save') => {
      saveIntentRef.current = intent;
    };

    if (isReadOnly) {
      return (
        <Flex justify="flex-end" mt={4}>
          <LockedActionButton reason="edit-templates">
            {isPublishedEdit ? 'Save changes' : 'Publish template'}
          </LockedActionButton>
        </Flex>
      );
    }

    if (isPublishedEdit) {
      return (
        <Flex justify="flex-end">
          <Button
            variant="gradient"
            type="submit"
            form="template-builder-form"
            mt={4}
            isLoading={isCreating}
            disabled={needsEntitySelection}
            onClick={() => setIntent('save')}
          >
            Save changes
          </Button>
        </Flex>
      );
    }

    return (
      <Flex justify="flex-end" gap={3} mt={4}>
        {!mustPublishForOnboarding && (
          <Button
            variant="secondary"
            type="submit"
            form="template-builder-form"
            isLoading={isCreating && saveIntentRef.current === 'draft'}
            isDisabled={isCreating || needsEntitySelection}
            onClick={() => setIntent('draft')}
          >
            Save draft
          </Button>
        )}
        <Button
          variant="gradient"
          type="submit"
          form="template-builder-form"
          isLoading={isCreating && saveIntentRef.current === 'publish'}
          isDisabled={isCreating || needsEntitySelection}
          onClick={(e) => {
            setIntent('publish');
            guardPublishClick(e, isLimitReached, () =>
              setPublishBlockedOpen(true)
            );
          }}
        >
          Publish template
        </Button>
      </Flex>
    );
  };

  return (
    <Container display="flex" flexDir="column" flex="1" pb={6}>
      <EnhancedDesignTryBanner
        isLoading={isEdit && isLoadingTemplate}
        setSelectedTab={setSelectedTab}
        setSelectedPassTab={handleSetSelectedPassTab}
        setSelectedMainTabIndex={setSelectedMainTabIndex}
      />
      <ErrorsBanner
        setSelectedTab={setSelectedTab}
        setSelectedPassTab={handleSetSelectedPassTab}
        setSelectedMainTabIndex={setSelectedMainTabIndex}
        setPendingExpandLocationIndex={setPendingExpandLocationIndex}
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      {isEdit && isLoadingTemplate ? (
        <Center py={20} flex="1">
          <Spinner size="xl" color="orangePrimary" />
        </Center>
      ) : (
        <>
          <Flex
            flexDirection={{ base: 'column', sm: 'row' }}
            alignItems="center"
            flexWrap="wrap"
            mt="15px"
            py="9px"
            minH="54px"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              w="full"
              gap={{ base: 4, sm: 10 }}
              direction={{ base: 'column', sm: 'row' }}
            >
              <Box>
                <Text textStyle="heading2" mb={2}>
                  {isEdit
                    ? selectedMainTabIndex === 1
                      ? `Edit ${templateName || templateResponse?.data?.attributes?.name} issuance form`
                      : `Edit ${templateName || templateResponse?.data?.attributes?.name}`
                    : selectedMainTabIndex === 1
                      ? 'Design your pass issuance form'
                      : templateName
                        ? `Create ${templateName}`
                        : 'Create a new pass template'}
                </Text>
                {isEdit ? (
                  <Text>uuid: {templateResponse?.data?.id}</Text>
                ) : selectedMainTabIndex === 1 ? (
                  <Text>
                    Create and personalise the form customers will complete to
                    receive their pass. Use this builder to customise how it
                    looks, what information you collect and how it connects to
                    the fields in your pass template.
                  </Text>
                ) : fromPreset ? (
                  <Text>
                    Everything is already set up – just replace the placeholder
                    content with your own branding and data.
                  </Text>
                ) : (
                  <Text>
                    Start by adding shared elements that appear on all devices,
                    then refine the Apple and Google versions with
                    platform-specific adjustments.
                    {templateType === 'stampCard' &&
                      ' For stamp cards, content can update as stamps are earned.'}
                  </Text>
                )}
              </Box>
              {selectedMainTabIndex === 0 &&
                (showDesignGuide ? (
                  <DesignGuideButton
                    onClick={onOpenDesignGuide}
                    disabled={needsEntitySelection}
                  />
                ) : (
                  <Button
                    variant="outlineRounded"
                    minW="150px"
                    flexShrink={0}
                    onClick={onOpenInspirationModal}
                    disabled={needsEntitySelection}
                  >
                    <Icon
                      as={CustomBulbIcon}
                      color="orangePrimary"
                      width="17px"
                      height="22px"
                    />
                    <Text ml={2}>Get inspired</Text>
                  </Button>
                ))}
            </Flex>
            <Spacer />
          </Flex>

          <Tabs index={selectedMainTabIndex} onChange={setSelectedMainTabIndex}>
            <Box
              my={6}
              mx="auto"
              opacity={needsEntitySelection ? 0.5 : 1}
              pointerEvents={needsEntitySelection ? 'none' : 'auto'}
            >
              <TabList>
                <Tab>Pass template</Tab>
                <Tab>Issuance form</Tab>
              </TabList>
            </Box>
            <TabPanels>
              <TabPanel
                px={0}
                opacity={needsEntitySelection ? 0.5 : 1}
                pointerEvents={needsEntitySelection ? 'none' : 'auto'}
              >
                <Box w="768px" h="61px" mb={10} mx="auto">
                  <BuilderTabs
                    tabs={BUILDER_DEVICE_TABS}
                    size="md"
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                  />
                </Box>
                <DemoPassContext.Provider value={{ isDemoPass, setIsDemoPass }}>
                  <Grid
                    templateColumns="minmax(0, 1fr) minmax(0, 1fr)"
                    gap={10}
                  >
                    <GridItem
                      minW={0}
                      {...readOnlyInertProps}
                      opacity={isReadOnly ? 0.6 : 1}
                    >
                      <BuilderPanel
                        passTabs={BUILDER_PASS_TABS}
                        selectedPassTab={selectedPassTab}
                        setSelectedPassTab={handleSetSelectedPassTab}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        isEdit={isEdit}
                        skipNoFormFieldsCheckRef={skipNoFormFieldsCheckRef}
                        onShowNoFormFieldsModal={() =>
                          setNoFormFieldsModalOpen(true)
                        }
                        onOpenInspirationModal={onOpenInspirationModal}
                        pendingExpandLocationIndex={pendingExpandLocationIndex}
                        setPendingExpandLocationIndex={
                          setPendingExpandLocationIndex
                        }
                        onPendingExpandLocationConsumed={
                          handlePendingExpandLocationConsumed
                        }
                        saveIntentRef={saveIntentRef}
                        publishBlockedOpen={publishBlockedOpen}
                        onPublishBlockedOpenChange={setPublishBlockedOpen}
                        googleRevertedPreview={googleRevertedPreview}
                        setGoogleRevertedPreview={setGoogleRevertedPreview}
                        isGooglePreviewActive={isGooglePreviewActive}
                        onSaveCompleteRef={onSaveCompleteRef}
                      />
                    </GridItem>
                    <GridItem minW={0}>
                      <Box
                        position={{ base: 'static', md: 'sticky' }}
                        top={{ md: 1 }}
                      >
                        <PreviewPanel
                          previewEntityId={isEdit ? templateOwnerId : undefined}
                          selectedPassTab={selectedPassTab}
                          setSelectedPassTab={handleSetSelectedPassTab}
                          selectedTab={selectedTab}
                          onEnhancedTabActiveChange={setIsEnhancedTabActive}
                          googleRevertedPreview={googleRevertedPreview}
                          setGoogleRevertedPreview={setGoogleRevertedPreview}
                          onGooglePreviewActiveChange={setIsGooglePreviewActive}
                        />
                        <InfoBannerQueue banners={infoBanners} />
                        {renderActionButtons()}
                      </Box>
                    </GridItem>
                  </Grid>
                </DemoPassContext.Provider>
              </TabPanel>
              <TabPanel
                px={0}
                opacity={needsEntitySelection ? 0.5 : 1}
                pointerEvents={needsEntitySelection ? 'none' : 'auto'}
              >
                {(isEdit &&
                  (issuanceFormStatusPending ||
                    isLoadingTemplate ||
                    !formHydratedFromApi)) ||
                (isCreating &&
                  hasDynamicForm &&
                  (!isEdit || formWasDisabledOnLoad)) ? (
                  <Center py={20} minH="400px">
                    <Spinner size="xl" color="orangePrimary" />
                  </Center>
                ) : (
                  <Grid
                    templateColumns="minmax(0, 1fr) minmax(0, 1fr)"
                    gap={10}
                  >
                    <GridItem
                      minW={0}
                      {...readOnlyInertProps}
                      opacity={isReadOnly ? 0.6 : 1}
                    >
                      <CustomFormBuilderPanel
                        isEdit={isEdit}
                        enableLocked={!canEditIssuanceForm}
                      />
                    </GridItem>
                    <GridItem minW={0}>
                      <Box
                        position={{ base: 'static', md: 'sticky' }}
                        top={{ md: 6 }}
                      >
                        <CustomFormPreviewPanel />
                        {renderActionButtons()}
                      </Box>
                    </GridItem>
                  </Grid>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
      <InspirationModal
        isOpen={isInspirationModalOpen}
        onClose={onCloseInspirationModal}
      />
      <DesignGuideModal
        isOpen={isDesignGuideOpen}
        onClose={onCloseDesignGuide}
      />
      <FormIsLiveModal
        isOpen={!!formCreateSuccessId}
        onClose={() => dispatch(setFormCreateSuccessId(null))}
      />
      <NoFormFieldsModal
        isOpen={noFormFieldsModalOpen}
        onClose={() => setNoFormFieldsModalOpen(false)}
        onGoBack={() => setNoFormFieldsModalOpen(false)}
        onContinueWithoutFields={() => {
          skipNoFormFieldsCheckRef.current = true;
          (
            document.getElementById(
              'template-builder-form'
            ) as HTMLFormElement | null
          )?.requestSubmit();
        }}
      />
      <UnsavedChangesModal
        isOpen={isBlocked}
        isSaving={isCreating}
        onClose={() => {
          onSaveCompleteRef.current = null;
          reset();
        }}
        onSaveAndExit={() => {
          onSaveCompleteRef.current = () => {
            markClean();
            proceed();
          };
          (
            document.getElementById(
              'template-builder-form'
            ) as HTMLFormElement | null
          )?.requestSubmit();
        }}
        onLeaveWithoutSaving={proceed}
      />
    </Container>
  );
};

export default PassTemplateBuilderPage;
